import app, { db } from './firebase';
import { isSupported, getMessaging, getToken, onMessage, type Messaging } from 'firebase/messaging';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';

export type PushEnableResult =
  | { status: 'enabled'; token: string }
  | { status: 'blocked' | 'denied'; token?: undefined }
  | { status: 'unsupported'; token?: undefined }
  | { status: 'missing-vapid-key'; token?: undefined }
  | { status: 'error'; error: unknown; token?: undefined };

let messagingSingleton: Messaging | null = null;
let foregroundListenerAttached = false;

function safeTokenDocId(token: string) {
  // Firestore doc IDs cannot contain '/'
  return token.replace(/[^A-Za-z0-9_-]/g, '_').slice(0, 1200);
}

async function getMessagingIfSupported() {
  const supported = await isSupported();
  if (!supported) return null;
  if (!messagingSingleton) messagingSingleton = getMessaging(app);
  return messagingSingleton;
}

async function ensureFcmServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  const swUrl = `${process.env.PUBLIC_URL}/firebase-messaging-sw.js`;
  const reg = await navigator.serviceWorker.register(swUrl);
  await navigator.serviceWorker.ready;
  return reg;
}

export async function enablePushNotifications(): Promise<PushEnableResult> {
  try {
    if (!('Notification' in window)) return { status: 'unsupported' };

    const permission = await Notification.requestPermission();
    if (permission === 'denied') return { status: 'denied' };
    if (permission !== 'granted') return { status: 'blocked' };

    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    if (!vapidKey) return { status: 'missing-vapid-key' };

    const messaging = await getMessagingIfSupported();
    if (!messaging) return { status: 'unsupported' };

    const swReg = await ensureFcmServiceWorker();
    if (!swReg) return { status: 'unsupported' };

    const token = await getToken(messaging, {
      vapidKey,
      serviceWorkerRegistration: swReg,
    });

    if (!token) return { status: 'error', error: new Error('No FCM token returned') };

    // Store token for broadcast notifications (no login required)
    const id = safeTokenDocId(token);
    await setDoc(
      doc(db, 'pushSubscribers', id),
      {
        token,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        userAgent: navigator.userAgent,
        language: navigator.language,
        scope: process.env.PUBLIC_URL || '/',
      },
      { merge: true }
    );

    // Attach foreground listener once (optional)
    if (!foregroundListenerAttached) {
      onMessage(messaging, (payload) => {
        // If user already granted notifications, show a notification while app is open.
        // (Some browsers may still suppress this; safe fallback is console log.)
        try {
          const n = payload?.notification;
          if (n?.title && Notification.permission === 'granted') {
            new Notification(n.title, {
              body: n.body,
              icon: n.icon,
            });
          }
        } catch {
          // ignore
        }
      });
      foregroundListenerAttached = true;
    }

    return { status: 'enabled', token };
  } catch (error) {
    return { status: 'error', error };
  }
}
